import * as React from 'react';
import { observer } from 'mobx-react';
import { Modal, Select } from '@ixicare/ui';
import { HcpGroup } from '@ixicare/resources';
import { useMutation, useQuery } from '@tanstack/react-query';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';

const HcpGroupSelector: React.FC = observer(() => {
  const { auth } = useStores();

  const { data: groups } = useQuery({
    queryKey: ['hcpGroupsAll'],
    queryFn: () => HcpGroup.getHcpGroups(),
  });

  const { data: siteConfig } = useSiteConfiguration();
  const isSelfAssignEnabled = siteConfig?.['enable-hcp-self-assign-hcp-group'] ?? false;

  const joinGroupMutation = useMutation({
    mutationFn: ({ groupId, hcpId }: { groupId: string; hcpId: number }) =>
      HcpGroup.assignHcpToHcpGroup(groupId, hcpId),
    onSuccess: () => {
      if (auth.user?.id) {
        void auth.fetchUser(auth.user.id);
      }
    },
    onError: (error) => {
      console.error('Error switching group:', error);
    },
  });

  const groupOptions = React.useMemo(
    () => groups?.map((g) => ({ value: g.id, label: g.hcpGroupName })) ?? [],
    [groups]
  );

  if (!auth.user || !isSelfAssignEnabled || !groups || groups.length === 0) {
    return null;
  }

  const handleChange = (groupId: string) => joinGroupMutation.mutate({ groupId, hcpId: auth.user!.id });

  return (
    <>
      <label style={{ whiteSpace: 'nowrap', marginBottom: 0 }}>{t('hcpGroups.currentGroup') + ':'}</label>
      <Select
        value={auth.user.hcpGroupId || null}
        onChange={handleChange}
        options={groupOptions}
        placeholder={t('hcpGroups.select.placeholder')}
        as="filter"
      />
      {!auth.user.hcpGroupId && (
        <Modal>
          <h3>{t('hcpGroups.currentGroup')}</h3>
          <p className="mb-20">{t('hcpGroups.modal.selectGroupPrompt')}</p>
          <div className="d-flex justify-content-end">
            <Select
              value={auth.user.hcpGroupId || null}
              onChange={handleChange}
              options={groupOptions}
              placeholder={t('hcpGroups.select.placeholder')}
              as="filter"
            />
          </div>
        </Modal>
      )}
    </>
  );
});

export default HcpGroupSelector;
